// Hideo - online saving from the Profiles window (Pinia): the status line under the section (#driveStatus), connecting
// and disconnecting a service, the service picker of "Save online" / "Load online" and the list of files of
// "Load online". The calls to the services (sign-in, Drive API) are src/features/cloud/ (services.ts, google.ts,
// dropbox.ts), the online save drive.ts, the autosave autosync.ts; their state (DRIVE, SYNC, the autosave pause, busy)
// is the sync store (src/stores/sync.ts), which the dialogs read directly.
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { t } from '../i18n'
import { CONFIG } from '../config'
import { uiConfirm, uiAlert } from './notices'
import { useDialogsStore } from './dialogs'
import { useUiStore } from './ui'
import { useSyncStore } from './sync'
import { sortFiles, type CloudFile } from '../features/cloud'
import { CS, cloudOn, driveOnline, saveDriveState } from '../features/cloud/common'
import { CLOUDS } from '../features/cloud/services'
import { loadGis } from '../features/cloud/google'
import { driveSave, linkLoaded, renderDriveUi, setAutoPaused } from '../features/cloud/drive'
import { mediaPrefetch, mediaRetry } from '../features/cloud/media'
import { applyLoadedFile, validProfileFile } from '../features/storage/files'

export const useCloudStore = defineStore('cloud', () => {
  const dialogs = useDialogsStore()
  const ui = useUiStore()
  const sync = useSyncStore()

  /** Line under the online-saving section: what is happening now ('' = hidden) */
  const status = ref('')
  function setStatus(text: string): void { status.value = text }

  /** An online operation failed: the message in a dialog */
  function error(e: unknown): void {
    const err = e as { message?: string; detail?: unknown } | null
    console.error(e, err && err.detail)
    setStatus('')
    uiAlert(err && err.message ? err.message : t('Online operation failed.'))
  }
  const onlineOnly = () => uiAlert(t('Online saving only works from the {site} website, not when you open the file from your computer.', { site: CONFIG.siteName }))

  // ===== Profiles window =====
  function openProfiles(): void {
    ui.closeMobileMenu()
    dialogs.show('profiles', {})
    // Google sign-in is loaded at once: it must be ready when a Drive button is clicked, otherwise the browser blocks Google's window
    if (driveOnline) loadGis().catch(() => {})
  }
  const closeProfiles = () => dialogs.hide('profiles')

  // ===== Connect / disconnect a service =====
  async function connect(pid: string): Promise<boolean> {
    const P = CLOUDS[pid]
    if (!driveOnline) { onlineOnly(); return false }
    if (!P.configured()) { uiAlert(t('{service} is not active in Hideo yet: coming soon.', { service: P.name })); return false }
    try {
      setStatus(t('Connecting to {service}…', { service: P.name }))
      const { email } = await P.connect()
      Object.assign(CS(pid), { connected: true, email: email || '' })
      saveDriveState()
      setAutoPaused('', pid)
      setStatus('')
      renderDriveUi()
      mediaRetry()
      return true
    } catch (e) { error(e); return false }
  }
  async function disconnect(pid: string): Promise<void> {
    const P = CLOUDS[pid]
    if (!(await uiConfirm(t('Disconnect {service} from this device?\nYour files on {service} stay where they are, but autosave stops.', { service: P.name }), { ok: t('Disconnect') }))) return
    try { P.disconnect() } catch { /* already signed out */ }
    sync.DRIVE.p[pid] = {}
    saveDriveState()
    renderDriveUi()
  }

  // ===== Service picker ("Save online" / "Load online") =====
  function openPick(action: 'save' | 'load', profileId: string): void {
    if (!driveOnline) return onlineOnly()
    dialogs.show('cloudPick', { action, profileId })
  }
  const closePick = () => dialogs.hide('cloudPick')
  async function pickGo(action: 'save' | 'load', profileId: string, pid: string): Promise<void> {
    closePick()
    if (!cloudOn(pid) && !(await connect(pid))) return
    if (action === 'save') void driveSave(profileId, false, pid)
    else await openLoad(profileId, pid)
  }

  // ===== Load online =====
  /** The profile being loaded into and its service; files = null while they are being read */
  const load = ref<{ profileId: string; pid: string; files: CloudFile[] | null; linked: string }>({ profileId: '', pid: 'google', files: null, linked: '' })
  async function openLoad(id: string, pid: string): Promise<void> {
    if (sync.busy) return
    const P = CLOUDS[pid]
    sync.busy = true
    try {
      await P.token(true)
      load.value = { profileId: id, pid, files: null, linked: '' }
      dialogs.show('driveFiles', { profileId: id, pid })
      const files = await P.listProfiles(), linked = CS(pid).links[id] || ''
      load.value = { profileId: id, pid, files: sortFiles(files, linked), linked }
    } catch (e) { closeLoad(); error(e) }
    finally { sync.busy = false }
  }
  const closeLoad = () => dialogs.hide('driveFiles')
  async function loadFile(fileId: string): Promise<void> {
    const { profileId: id, pid } = load.value, P = CLOUDS[pid]
    if (!id || sync.busy) return
    sync.busy = true
    try {
      setStatus(t('Downloading from {service}…', { service: P.name }))
      let d: unknown = null
      try { d = JSON.parse(await P.readText(fileId)) } catch (e) { if ((e as { status?: number }).status) throw e }
      if (!validProfileFile(d)) throw new Error(t('The file on {service} is not a valid Hideo profile.', { service: P.name }))
      closeLoad()
      setStatus('')
      if (await applyLoadedFile(id, d, 'online-caricato', P.name)) {
        const m = (load.value.files || []).find(f => f.id === fileId), saved = (d as { savedAt?: unknown }).savedAt
        linkLoaded(pid, id, fileId, (m && m.version) || (typeof saved === 'string' ? saved : ''))
        setAutoPaused('', pid)
        // Images and voice notes are downloaded right away, while the sign-in is valid (then they stay on the device)
        setStatus(t('Downloading images and voice notes…'))
        await mediaPrefetch(d as Parameters<typeof mediaPrefetch>[0])
        mediaRetry()
        setStatus(t('Loaded from {service}', { service: P.name }))
      }
    } catch (e) { error(e) }
    finally { sync.busy = false }
  }

  return { status, setStatus, error, openProfiles, closeProfiles, connect, disconnect, openPick, closePick, pickGo, load, openLoad, closeLoad, loadFile }
})
